import { createContext, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";

const LanguageContext = createContext(null);

const EN = {
  // Navigation
  Start: "Home",
  Kontakt: "Contact",
  Anfrage: "Enquiry",
  "Menü öffnen": "Open menu",
  "Menü schließen": "Close menu",
  Menü: "Menu",
  Schließen: "Close",
  Bilder: "Images",
  Bild: "Image",
  "Noch keine Bilder. Sie erscheinen, sobald Serien eine Rubrik tragen.":
    "No images yet. They appear once a series carries a category.",
  Startseite: "Homepage",
  "Hamburg · Buchungen 2026/2027": "Hamburg · Bookings 2026/2027",
  "Über mich": "About",
  "Ich bin": "I'm",
  "Fotograf aus Hamburg.": "A photographer based in Hamburg.",
  "Fotograf aus Hamburg": "Photographer based in Hamburg",
  "Wim Jansen Delgado, Fotograf aus Hamburg": "Wim Jansen Delgado, photographer based in Hamburg",
  "Die besten Momente lassen sich nicht": "The best moments can't be",
  "planen.": "planned.",
  "Ich bin da, wenn sie passieren.": "I'm there when they happen.",
  "L'Aliança d'Anglès, Fotografie von Wim Jansen Delgado": "L'Aliança d'Anglès, photography by Wim Jansen Delgado",
  "El Celler de Can Roca, Fotografie von Wim Jansen Delgado": "El Celler de Can Roca, photography by Wim Jansen Delgado",
  "Haerlin, Fotografie von Wim Jansen Delgado": "Haerlin, photography by Wim Jansen Delgado",
  "Referenzen: Michelin Guide, Der Feinschmecker, The Fontenay, El Celler de Can Roca u. a.":
    "References: Michelin Guide, Der Feinschmecker, The Fontenay, El Celler de Can Roca and more.",
  "Viele Jahre habe ich in der Werbung gearbeitet, zuletzt bei Apple. Mich hat dabei immer interessiert, warum sich etwas richtig anfühlt. Irgendwann wurde die Kamera für mich der ehrlichste Weg, das herauszufinden.":
    "For years I worked in advertising, most recently at Apple. I was always curious about what makes something feel right. At some point, the camera became the most honest way to find out.",
  "Mich zieht an, was oft übersehen wird: das Ungestellte, das Ungeplante, der stille Moment, bevor jemand die Kamera bemerkt.":
    "I'm drawn to what is often overlooked: the unposed, the unplanned, the quiet moment before someone notices the camera.",
  "Meine Arbeit bewegt sich zwischen Restaurants, Hotels und Reisen, nicht weil ich einem Genre folge, sondern weil ich mich dort am lebendigsten fühle: an einem guten Tisch, an einem fremden Ort, mit Menschen, die lieben, was sie tun.":
    "My work moves between restaurants, hotels and travel. Not because I follow a genre, but because that's where I feel most alive: around a good table, in an unfamiliar place, with people who care deeply about what they do.",
  "Restaurants sind für mich Orte, an denen die Zeit stehen bleibt. Das Licht, die Räume, das Essen und die Menschen inspirieren mich. Ob mit meiner Familie am Tisch oder unter Köchen am Pass, egal wo auf der Welt: Dort komme ich zur Ruhe, dort fühle ich mich zu Hause. Genau dieses Gefühl halte ich in meinen Bildern fest.":
    "To me, restaurants are places where time stands still. The light, the spaces, the food and the people all inspire me. Whether at the table with my family or among chefs at the pass, anywhere in the world, that is where I slow down and feel at home. That feeling is exactly what I try to capture in my pictures.",
  "Abseits der Kamera ist das Leben herrlich chaotisch: meine Frau, zwei Kinder, selten ein stiller Moment, außer manchmal am Restauranttisch. Ich möchte es nicht anders haben.":
    "Away from the camera, life is delightfully chaotic: my wife, two kids and rarely a quiet moment, except sometimes at a restaurant table. I wouldn't have it any other way.",
  "Momente, die nicht nach Aufmerksamkeit fragen,": "Moments that don't ask for attention,",
  "aber bei dir bleiben.": "but stay with you.",
  // Footer
  "Gefunden,": "Found,",
  "nicht inszeniert.": "not staged.",
  "Ich bin Wim, Fotograf aus Hamburg. Mich ziehen gute Tische, fremde Orte und die Momente an, die niemand arrangiert hat.":
    "I'm Wim, a photographer based in Hamburg, drawn to good tables, unfamiliar places and the moments nobody arranged.",
  "© 2026 Wim Jansen Delgado · Alle Rechte vorbehalten": "© 2026 Wim Jansen Delgado · All rights reserved",
  Impressum: "Imprint",
  Datenschutz: "Privacy",
  // Home
  "Wim Jansen Delgado": "Wim Jansen Delgado",
  Restaurants: "Restaurants",
  Hotels: "Hotels",
  Reisen: "Travel",
  "Ein paar Favoriten, 2023 bis 2026": "A few favourites, 2023 to 2026",
  "Wie ich arbeite": "My approach",
  "So arbeite ich": "How I work",
  "Erstes Gespräch": "First conversation",
  "Wir sprechen kurz über dein Haus, den Anlass und wofür du die Bilder brauchst. Danach bekommst du ein klares Angebot.":
    "We talk briefly about your place, the occasion and what you need the pictures for. You then receive a clear quote.",
  "Der Tag vor Ort": "The day itself",
  "Ich arbeite leise und mit wenig Ausrüstung. Niemand muss posieren, dein Team arbeitet einfach weiter.":
    "I work quietly and with little equipment. Nobody has to pose. Your team simply carries on.",
  "Auswahl und Bearbeitung": "Selection and editing",
  "Ich wähle die stärksten Bilder aus und bearbeite jedes einzeln. Du bekommst sie in voller Auflösung, fertig für Web und Druck.":
    "I choose the strongest pictures and edit each one individually. You receive them in full resolution, ready for web and print.",
  "Nutzungsrechte": "Usage rights",
  "Vorab halten wir schriftlich fest, wofür du die Bilder nutzen darfst: Website, Social Media, Presse oder mehr. So gibt es später keine Überraschungen.":
    "Before we start, we agree in writing where you can use the pictures: website, social media, press or beyond. That way there are no surprises later.",
  "Kundenstimmen": "Kind words",
  "Neueste Fieldnote": "Latest fieldnote",
  Weiterlesen: "Read more",
  "Erzähl mir von deinem Projekt.": "Tell me about your project.",
  "Ich mache Bilder,": "I make pictures",
  "die bleiben.": "that stay.",
  "Mich interessiert das Unposierte, das Ungeplante: der stille Moment, kurz bevor jemand die Kamera bemerkt.":
    "I'm drawn to the unposed, the unplanned: the quiet moment before someone becomes aware of the camera.",
  Ausgewählte: "Selected",
  Arbeiten: "works",
  "Alle ansehen": "View all",
  "Auftraggeber & Partner": "Clients & partners",
  "Schreib mir": "Write to me",
  "Diese Website verwendet nur technisch notwendige Cookies.":
    "This website only uses technically necessary cookies.",
  "Mehr erfahren": "Learn more",
  Fieldnotes: "Fieldnotes",
  "Notizen von unterwegs": "Notes from the road",
  "Kleine Geschichten hinter den Bildern. Von Aufträgen, von unterwegs und vom Familientisch.":
    "Small stories behind the pictures. From assignments, from the road and from the family table.",
  "Berührt mich": "This moved me",
  "Danke.": "Thank you.",
  Projekte: "Projects",
  Porträts: "Portraits",
  Menschen: "People",
  "Neues Menü": "New menu",
  "Ausgewählte Serien": "Selected series",
  "Ich aktualisiere gerade die Projekte.": "I'm currently updating the projects.",
  "Wird gerade neu gestaltet": "Being redesigned right now",
  "Küchen, Tische, Menschen": "Kitchens, tables, people",
  "Zimmer, Lobbys, stille Ecken": "Rooms, lobbies, quiet corners",
  "Küsten, Straßen, weite Blicke": "Coasts, roads, open views",
  "Alle Projekte": "All projects",
  Bild: "image",
  Bilder: "images",
  "Vorheriges Bild": "Previous image",
  "Nächstes Bild": "Next image",
  Übersicht: "Overview",
  Slideshow: "Slideshow",
  "Diese Seite entsteht gerade. Die ersten Serien folgen in Kürze.":
    "This page is in the making. The first series will follow shortly.",
  "Noch keine Projekte. Sie entstehen, sobald Arbeiten ein Projekt tragen.":
    "No projects yet. They appear once works are assigned to a project.",
  "Dieses Projekt hat noch keine Bilder.": "This project has no images yet.",
  "Alle Notizen": "All notes",
  "Noch keine Notizen. Die erste kommt bald.": "No notes yet. The first one is coming soon.",
  // Portfolio
  // Contact
  "Erzähl mir von": "Tell me about",
  "deinem Projekt.": "your project.",
  "Ob Restaurant, Hotel, Reisereportage oder eine ganz eigene Idee: Schreib mir einfach. Ich lese jede Nachricht persönlich und antworte in der Regel innerhalb von 48 Stunden. Hilfreich sind ein paar Eckdaten: Ort, Zeitraum und wofür du die Bilder brauchst.":
    "Whether it's a restaurant, a hotel, a travel story or an idea entirely your own: just write to me. I read every message personally and usually reply within 48 hours. It helps to know the location, the timing and what the pictures are for.",
  "E-Mail": "Email",
  Telefon: "Phone",
  "Ich habe 2026/2027 noch Zeit für dich": "I still have time for you in 2026/2027",
  "Name *": "Name *",
  "E-Mail *": "Email *",
  "Projektart *": "Project type *",
  "Budget (optional)": "Budget (optional)",
  "Nachricht *": "Message *",
  "Dein Name": "Your name",
  "name@beispiel.de": "name@example.com",
  Passwort: "Password",
  Ansehen: "View",
  "Das Passwort stimmt nicht.": "That password is not correct.",
  "Bitte wählen": "Please select",
  "Erzähl mir von deinem Vorhaben: Anlass, Zeitraum, Ort …": "Tell me about your project: occasion, timeframe, location …",
  "Wird gesendet …": "Sending …",
  "Vielen Dank! Ich melde mich persönlich bei dir. Versprochen.": "Thank you! I'll get back to you personally. Promise.",
  "Anfrage senden": "Send enquiry",
  Einzelauftrag: "Single assignment",
  "Monatliche Begleitung": "Monthly collaboration",
  "Editorial & Reise": "Editorial & travel",
  "Etwas anderes": "Something else",
  Bretagne: "Brittany",
  "Projekt anfragen": "Start a project",
  "Nächstes Projekt": "Next project",
  Zusammenarbeit: "Working together",
  "Weitere Serien folgen": "More series to come",
  "Inhalte konnten nicht geladen werden.": "Content could not be loaded.",
  "Erneut versuchen": "Try again",
  "Diese Seite wird gerade überarbeitet.": "This page is being updated.",
  "Schau bald wieder vorbei.": "Please check back soon.",
  "Zur Startseite": "Back to home",
  "Mit dem Absenden werden deine Angaben verarbeitet, um deine Anfrage zu beantworten. Mehr dazu in der":
    "By sending this form, your details will be processed to handle your enquiry. More in the",
  Datenschutzerklärung: "privacy policy",
  "unter 1.000 €": "under €1,000",
  "1.000 bis 5.000 €": "€1,000 to €5,000",
  "5.000 bis 10.000 €": "€5,000 to €10,000",
  "über 10.000 €": "over €10,000",
};

// Pfad-Zuordnung DE ↔ EN für eigene englische Adressen
const EN_PATHS = {
  "/": "/en",
  "/projekte": "/en/projects",
  "/fieldnotes": "/en/fieldnotes",
  "/ueber-mich": "/en/about",
  "/kontakt": "/en/contact",
  "/impressum": "/en/imprint",
  "/datenschutz": "/en/privacy",
};
const DE_PATHS = Object.fromEntries(Object.entries(EN_PATHS).map(([de, en]) => [en, de]));

export const localizedPath = (path, lang) => {
  if (lang !== "en") return path;
  if (path.startsWith("/projekte/")) return path.replace("/projekte/", "/en/projects/");
  return EN_PATHS[path] ?? path;
};

// Zieladresse beim Sprachwechsel: jeweils dieselbe Seite in der anderen Sprache
export const switchPath = (pathname, targetLang) => {
  if (targetLang === "en") {
    if (pathname.startsWith("/projekte/")) return pathname.replace("/projekte/", "/en/projects/");
    return EN_PATHS[pathname] ?? "/en";
  }
  if (pathname.startsWith("/en/projects/")) return pathname.replace("/en/projects/", "/projekte/");
  return DE_PATHS[pathname] ?? "/";
};

// Die Sprache folgt ausschließlich der Adresse: deutsche Pfade = DE, /en/* = EN.
// Es wird nichts gespeichert (kein localStorage) — die URL gewinnt immer.
export const LanguageProvider = ({ children }) => {
  const { pathname } = useLocation();
  const lang = pathname === "/en" || pathname.startsWith("/en/") ? "en" : "de";

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (s) => (lang === "en" ? EN[s] ?? s : s);

  return (
    <LanguageContext.Provider value={{ lang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
